/**
 * NotificationService handles all notification API interactions.
 * Follows SOLID and DRY principles for maintainability and testability.
*/
import { NOTIFICATION_SERVICE_ERRORS } from './util/constants';

function NotificationService(store, url, includeCredentials) {
    const baseUrl = `${url}/api/public/v1/notifications`;
    this.baseUrl = baseUrl;
    this.includeCredentials = !!includeCredentials;
}

/**
 * Generic fetch wrapper for API calls.
 */
NotificationService.prototype.apiRequest = function(endpoint, options) {
    options = options || {};
    var headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    };
    if (options.headers) {
        for (var key in options.headers) {
            if (Object.prototype.hasOwnProperty.call(options.headers, key)) {
                headers[key] = options.headers[key];
            }
        }
    }

    var fetchOptions = Object.assign({}, options, { headers: headers });
    if (this.includeCredentials) {
        fetchOptions.credentials = 'include';
    }

    return fetch(`${this.baseUrl}${endpoint}`, fetchOptions)
        .then(function(response) {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            // Handle empty response (e.g., 204 or empty body)
            var contentType = response.headers.get('content-type');
            if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
                return null;
            }
            return response.text().then(function(text) {
                return text ? JSON.parse(text) : null;
            });
        })
        .catch(function(error) {
            if (!notificationServiceErrorShown) {
                console.error(NOTIFICATION_SERVICE_ERRORS.API_DOWN);
                notificationServiceErrorShown = true;
            }
            throw error;
        });
};

/**
 * Fetch notifications for the current user.
 */
NotificationService.prototype.fetchNotifications = function(paramsObj) {
    paramsObj = paramsObj || {};
    var params = new URLSearchParams({
        includeAcknowledged: paramsObj.includeAcknowledged || false,
        offset: paramsObj.offset || 0,
        limit: paramsObj.limit || 50
    });
    return this.apiRequest(
        `/recipient/notifications?${params.toString()}`,
        { method: 'GET'}
    );
};

/**
 * Mark a notification as read.
 */
NotificationService.prototype.markAsRead = function(id) {
    return this.apiRequest(
        `/${id}/acknowledge`,
        { method: 'PATCH'}
    );
};

/**
 * Mark a notification as unread.
 */
NotificationService.prototype.markAsUnread = function(id) {
    return this.apiRequest(
        `/${id}/unacknowledge`,
        { method: 'PATCH'}
    );
};

/**
 * Dismiss a notification.
 */
NotificationService.prototype.dismiss = function(id) {
    return this.apiRequest(
        `/${id}`,
        { method: 'DELETE' }
    );
};


function isNotificationUnread(acknowledgeAtUtc) {
	const MAX_ACK_DATE = '9999-12-31T23:59:59.9999999Z';
    return acknowledgeAtUtc === MAX_ACK_DATE;
}

/**
 * Main interface for notification operations, exposed globally for mocking/testing.
 */
export function notificationInterfaceFn(store, notificationBaseUrl, includeCredentials) {
    var notificationService = new NotificationService(store, notificationBaseUrl, includeCredentials);

    window.NotificationServiceInterface = {
        /**
         * Get notifications for the user.
         */
        get: function () {
            return notificationService.fetchNotifications().then(function(data) {
                var results = data && data.results ? data.results : [];
                var updatedNotificationItems = results.map(function(n) {
                    return {
                        title: n.subject,
                        content: n.content,
                        unread: isNotificationUnread(n.acknowledgeAtUtc),
                        timestamp: n.createdAtUtc ? new Date(n.createdAtUtc).toLocaleString() : null,
                        id: n.id
                    };
                });
                return updatedNotificationItems;
            });
        },
        /**
         * Mark a notification as read.
         */
        markAsRead: function (id) {
            return notificationService.markAsRead(id)
                .then(function() { return 'Notification marked as read'; })
                .catch(function(error) {
                    return Promise.reject(`Failed to mark notification ${id} as read`);
                });
        },
        /**
         * Mark a notification as unread.
         */
        markAsUnread: function (id) {
            return notificationService.markAsUnread(id)
                .then(function() { return 'Notification marked as un-read'; })
                .catch(function(error) {
                    return Promise.reject(`Failed to mark notification ${id} as un-read`);
                });
        },
        /**
         * Dismiss a notification.
         */
        dismiss: function (id) {
            return notificationService.dismiss(id)
                .then(function() { return 'Notification marked as Dismissed'; })
                .catch(function(error) {
                    return Promise.reject(`Failed to mark notification ${id} as Dismissed`);
                });
        }
    };
}

let notificationServiceErrorShown = false;
